import type { ContentAssignmentDTO, ContentVersionDTO } from '@today/types'
import { EXPIRY_DAYS, isInForce } from './text-list-rules'

/** Assignment pickers, ordering and warnings (TO-420 content-admin-ui REQ-010/REQ-012). */

export type InheritedAssignment = ContentAssignmentDTO & { replacedByTextId: string | null }

export type TextWarning =
	| { kind: 'missing'; code: string; locales: string[] }
	| { kind: 'expiring'; code: string; locale: string; date: string }

export const SORT_STEP = 10
export const SORT_MAX = 10_000
const DAY_MS = 86_400_000

/** Texts with a published or approved localization: merged, deduped, by code, minus `exclude`. */
export const pickerCandidates = <T extends { id: string; code: string }>(
	published: readonly T[],
	approved: readonly T[],
	exclude: ReadonlySet<string>,
): T[] => {
	const byId = new Map<string, T>()
	for (const text of [...published, ...approved]) {
		if (!exclude.has(text.id) && !byId.has(text.id)) byId.set(text.id, text)
	}
	return [...byId.values()].sort((a, b) => a.code.localeCompare(b.code))
}

/** The next position: highest `sortOrder` + 10 (10 when empty), capped at 10000. */
export const nextSortOrder = (list: readonly { sortOrder: number }[]): number =>
	Math.min(list.reduce((max, a) => Math.max(max, a.sortOrder), 0) + SORT_STEP, SORT_MAX)

/** Inherited tenant texts a product text may replace: not replaced yet, or replaced by `self`. */
export const replaceOptions = (inherited: readonly InheritedAssignment[], self?: string): InheritedAssignment[] =>
	inherited.filter((a) => a.replacedByTextId === null || a.replacedByTextId === self)

/** By `sortOrder`, then code. */
export const sortAssignments = <T extends { sortOrder: number; textCode: string }>(list: readonly T[]): T[] =>
	[...list].sort((a, b) => a.sortOrder - b.sortOrder || a.textCode.localeCompare(b.textCode))

/** Missing required languages, and in-force localizations ending within 30 days. */
export const textWarnings = (
	texts: readonly { code: string; missingLocales: readonly string[]; versions: readonly ContentVersionDTO[] }[],
	now: Date,
): TextWarning[] =>
	texts.flatMap((text) => {
		const warnings: TextWarning[] = []
		if (text.missingLocales.length) {
			warnings.push({ kind: 'missing', code: text.code, locales: [...text.missingLocales] })
		}
		const horizon = now.getTime() + EXPIRY_DAYS * DAY_MS
		for (const v of text.versions) {
			for (const l of v.localizations) {
				if (isInForce(l, now) && l.validTo !== null && new Date(l.validTo).getTime() <= horizon) {
					warnings.push({ kind: 'expiring', code: text.code, locale: l.locale, date: l.validTo })
				}
			}
		}
		return warnings
	})

/**
 * Move the row at `from` to `to`, renumber `10, 20, 30, …`, and list the PATCH
 * of every row whose `sortOrder` changed. An out-of-range move is a no-op.
 */
export const reorder = <T extends { id: string; sortOrder: number }>(
	list: readonly T[],
	from: number,
	to: number,
): { ordered: T[]; patches: { id: string; sort_order: number }[] } => {
	if (from < 0 || to < 0 || from >= list.length || to >= list.length) return { ordered: [...list], patches: [] }
	const moved = [...list]
	const [row] = moved.splice(from, 1)
	moved.splice(to, 0, row as T)
	const ordered = moved.map((r, i) => ({ ...r, sortOrder: SORT_STEP * (i + 1) }))
	const before = new Map(list.map((r) => [r.id, r.sortOrder]))
	const patches = ordered
		.filter((r) => before.get(r.id) !== r.sortOrder)
		.map((r) => ({ id: r.id, sort_order: r.sortOrder }))
	return { ordered, patches }
}
